import type { ApiResponse } from '@/shared/api/types/api-response'
import type {
  AuthApiEnvelope,
  LoginApiRequest,
  LoginPayload,
  LoginResponse,
  TokenResponse,
  UserDTO,
} from '@/shared/api/types/auth-types'
import { GW } from '@/shared/api/endpoints'
import { mapTokenToUser, mergeUserProfileWithToken } from '@/shared/api/utils/auth-token'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { getTenantSlugFromLoginPath } from '@/shared/libs/admin-routes'
import { getStoredToken } from '@/shared/libs/get-auth-token'
import { request } from '@/shared/libs/request'

const AUTH_LOGOUT_PATH = 'auth/logout'
const AUTH_LOGIN_PATH = 'auth/login'

const USERS_ME_PATH = 'users/me'

function normalizeHeaderValue(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const normalized = value.trim()
  if (!normalized || normalized.toLowerCase() === 'none') {
    return undefined
  }
  return normalized
}

export async function login(payload: LoginPayload): Promise<LoginResponse> {
  const tenantSlug = payload.tenantSlug ?? getTenantSlugFromLoginPath(window.location.pathname)
  const requestPayload: LoginApiRequest = {
    username: payload.account,
    password: payload.password,
    tenantSlug,
  }

  const response = await request.post<AuthApiEnvelope<TokenResponse>>(
    GW.iam(AUTH_LOGIN_PATH),
    requestPayload,
  )
  const tokenData = unwrapApiEnvelope(response.data)

  if (!tokenData.accessToken || !tokenData.refreshToken) {
    throw new Error('Login succeeded but token payload is missing')
  }

  const defaultTenantId = normalizeHeaderValue(tokenData.defaultTenantId)
  const languageCode = normalizeHeaderValue(tokenData.lang)
  return {
    user: mapTokenToUser(tokenData.accessToken, payload.account, { role: tokenData.role }),
    token: tokenData.accessToken,
    refreshToken: tokenData.refreshToken,
    ...(defaultTenantId !== undefined ? { defaultTenantId } : {}),
    ...(languageCode !== undefined ? { languageCode } : {}),
  }
}

export async function logout(): Promise<void> {
  await request.post(GW.iam(AUTH_LOGOUT_PATH))
}

export async function getCurrentUser(): Promise<ApiResponse<LoginResponse['user']>> {
  const response = await request.get<AuthApiEnvelope<UserDTO>>(GW.packaging(USERS_ME_PATH))
  const userDto = unwrapApiEnvelope(response.data)
  const accessToken = getStoredToken()

  return {
    data: mergeUserProfileWithToken(userDto, accessToken),
  }
}
